import { useCallback, useMemo, useRef } from "react";
import { proxy, useSnapshot } from "valtio";
import { usePreviewContext } from "../../../contexts/preview-context";
import { useAppContext } from "../../../hooks/useAppContext";
import { useEditor } from "../../../hooks/useEditor";
import { transformPoint } from "../../../transform";
import { useGuidelineOverlay } from "../../guideline-overlay/use-guideline-overlay";
import { TransformType, useSnapping } from "./use-snapping";

/** Allowed region in DIP */
export function getAllowedZoneRegion(selectedZone: eXpress.core.IZone) {
	return selectedZone.spread.pageZones
		.filter((p) => p.canChangeDesign())
		.map((p) => p.spreadOuterBounds)
		.reduce<eXpress.math.Rect | undefined>(
			(combined, p) => (combined ? combined.union(p) : p),
			undefined,
		);
}

/** Disallowed region in DIP */
export function getDisallowedZoneRegion(selectedZone: eXpress.core.IZone) {
	return selectedZone.spread.pageZones
		.filter((p) => !p.canChangeDesign())
		.map((p) => p.spreadOuterBounds)
		.reduce<eXpress.math.Rect | undefined>(
			(combined, p) => (combined ? combined.union(p) : p),
			undefined,
		);
}

/** Shared state */
const zoneMovingState = proxy({
	isZoneMoving: false,
});

/**
 * Logic to move a zone
 * This intentionally doesn't include the logic to trigger it
 * Could be based on gestures, keyboard, ...
 */
export function useZoneMove() {
	/** The start location of dragging (In DIP so it is independent of scale panning) */
	const zoneMoveRef = useRef<
		| {
				selectedZone: eXpress.core.ITransformableZone;
				/** Distance from top/left of zone to finger (in DIP) */
				touchOffsetInDip: eXpress.math.Vector;
				/** The zone transform at the start */
				zoneTransform: eXpress.core.IZoneTransform;
				/** Remember our command row Id to know if we can finalize/undo it at the end */
				undoRowId: string | undefined;
				/** The region were we can place out zone */
				allowedRegion: eXpress.math.Rect | undefined;
				/** The region were no zone can be placed */
				disallowedRegion: eXpress.math.Rect | undefined;
				/** Log once during drag */
				hasLoggedDisallowedRegionError?: boolean;
		  }
		| undefined
	>();

	const { getScreenToSpreadTransform } = usePreviewContext();
	const { commandManager, client } = useEditor();

	const { googleAnalytics } = useAppContext();
	const { isZoneMoving } = useSnapshot(zoneMovingState);

	const snapTransform = useSnapping();
	const [lines, useLines] = useGuidelineOverlay();

	const start = useCallback(
		function startZoneMoving({
			selectedZone,
			pointOnScreen,
		}: {
			selectedZone: eXpress.core.ITransformableZone;
			pointOnScreen: eXpress.math.Point;
		}) {
			// Get DIP coordinate touched
			const screenToSpreadDip = getScreenToSpreadTransform(
				selectedZone.spread,
				undefined,
			)?.inverse();
			const touchDip = screenToSpreadDip
				? transformPoint(pointOnScreen, screenToSpreadDip)
				: undefined;

			zoneMovingState.isZoneMoving = true;
			useLines.setIsZoneMoving(true);
			zoneMoveRef.current = touchDip
				? {
						selectedZone,
						touchOffsetInDip: touchDip.from(selectedZone),
						zoneTransform: selectedZone.transform(),
						undoRowId: undefined,
						allowedRegion: getAllowedZoneRegion(selectedZone),
						disallowedRegion: getDisallowedZoneRegion(selectedZone),
				  }
				: undefined;
		},
		[getScreenToSpreadTransform, useLines],
	);

	const update = useCallback(
		function zoneMoving(pointOnScreen: eXpress.math.Point) {
			const {
				touchOffsetInDip,
				zoneTransform,
				hasLoggedDisallowedRegionError,
				selectedZone,
				allowedRegion,
				disallowedRegion,
			} = zoneMoveRef.current || {};
			if (!touchOffsetInDip || !zoneTransform || !selectedZone || !allowedRegion) return;

			// Get DIP Coordinate touched
			const screenToSpreadDip = getScreenToSpreadTransform(
				selectedZone.spread,
				undefined,
			)?.inverse();
			const pointerInDip = screenToSpreadDip
				? transformPoint(pointOnScreen, screenToSpreadDip)
				: undefined;
			if (!pointerInDip) return;

			// Displacement in DIP for drag start
			const displacementInDip = pointerInDip
				.subVector(touchOffsetInDip)
				.subPoint(zoneTransform.getPosition());

			useLines.setIsZoneMoving(true);
			// Move to the new position
			let newZoneTransform = zoneTransform.clone() as eXpress.core.ZoneTransform;
			let zoneAsFrame = zoneTransform
				.getParallelogram() // Get Frame representing the zone (with rotation)
				.translate(displacementInDip);

			// Move the zone by the closest path to the allowed region.
			if (!zoneAsFrame.overlaps(allowedRegion.toFrame())) {
				const zoneEdges = zoneAsFrame.edges();
				const allowedRegionEdges = allowedRegion.edges();

				// eslint-disable-next-line @typescript-eslint/no-non-null-assertion
				const closesZoneDisplacementToAllowedRegion = zoneEdges
					.flatMap((zoneEdge) =>
						allowedRegionEdges.map((region) => zoneEdge.displacementTo(region)),
					) // displacements from all edges
					.sort((v1, v2) => v1.length() - v2.length()) // find the closest displacement
					.first()!;

				zoneAsFrame = zoneAsFrame.translate(closesZoneDisplacementToAllowedRegion);
			}

			// TODO: prevent moving to a 'disallowed' region
			// Use case: a multi-page product where one page is fixed
			// We could skip if the update if the new zone position is on a disallowed zone, but then we:
			// - cannot be moved from a disallowed zone to an allowed zone
			// - will not always be at the DIP limit of the maximum/minimum size
			if (
				!hasLoggedDisallowedRegionError &&
				disallowedRegion &&
				zoneAsFrame.overlaps(disallowedRegion.toFrame())
			) {
				const error = new eXpress.NotSupportedException(
					`Moving a zone on a multipage spread '${selectedZone.spread?.toString()}' where a page is fixed.`,
				);
				client.logErrorAsync(error, false);
				if (zoneMoveRef.current) zoneMoveRef.current.hasLoggedDisallowedRegionError = true;
			}

			// Update zoneTransform with the new position
			newZoneTransform.setPosition(zoneAsFrame.getTranslation());

			if (snapTransform) {
				const newTransform = snapTransform(newZoneTransform, lines, TransformType.Move);
				if (!newTransform.isSimilarTo(newZoneTransform)) {
					newZoneTransform.left = newTransform.left;
					newZoneTransform.top = newTransform.top;

					newZoneTransform = newZoneTransform.snapToDips();
				}
			}
			newZoneTransform.left = Math.round(newZoneTransform.left);
			newZoneTransform.top = Math.round(newZoneTransform.top);
			newZoneTransform.width = Math.round(newZoneTransform.width);
			newZoneTransform.height = Math.round(newZoneTransform.height);

			const command = eXpress.core.commands.createTranslateZoneCommand(
				selectedZone,
				newZoneTransform,
				true,
			);
			// When dragging, we can receive events faster than the screen can show (faster than refresh rate of the screen)
			// I tested with RAF throttling but that caused a noticable delay when dragging, did not use RAF throttling
			commandManager.exec(command);

			if (zoneMoveRef.current) zoneMoveRef.current.undoRowId = commandManager.getRowId();
		},
		[getScreenToSpreadTransform, useLines, snapTransform, commandManager, client, lines],
	);

	const end = useCallback(
		function endZoneMoving() {
			if (zoneMoveRef.current?.undoRowId === commandManager.getRowId()) {
				commandManager.makeLastNonInteractive();
				googleAnalytics.trackEventToGa({
					trackingAction: "TextZoneMoved",
				});
			}
			useLines.setIsZoneMoving(false);
			zoneMovingState.isZoneMoving = false;
			zoneMoveRef.current = undefined;
		},
		[commandManager, googleAnalytics, useLines],
	);

	const cancel = useCallback(
		function cancelZoneMoving() {
			// Undo if last command is our move
			if (zoneMoveRef.current?.undoRowId === commandManager.getRowId()) {
				commandManager.undo(true);
			}
			zoneMovingState.isZoneMoving = false;
			zoneMoveRef.current = undefined;
		},
		[commandManager],
	);

	return [
		isZoneMoving,
		useMemo(
			// Try to return a stable object
			() => ({
				start,
				update,
				end,
				cancel,
			}),
			[start, update, end, cancel],
		),
	] as const;
}
